import { useCallback, useEffect, useRef, useState } from "react";
import { MSG_CONFIRM_DELETE, TRAFFIC_MONITORING_PROPERTIES } from "../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faTrash } from "@fortawesome/free-solid-svg-icons";
import InputThreshold from "./InputThreshold";

function CameraSetting(props) {
    const order = props.order; // one of [0, 1]
    const directionIndex = props.directionIndex;
    const cameraSetting = props.cameraSetting || {};
    const isEditable = props.isEditable;
    const [formData, setFormData] = useState({});
    const [cameraDropdownOpen, setCameraDropdownOpen] = useState({});
    const [cameraSearchTerm, setCameraSearchTerm] = useState({});
    const cameraDropdownRefs = useRef({});
    const cameraSearchInputRefs = useRef({});
    const [scenarioOptions, setScenarioOptions] = useState([]);

    useEffect(() => {
        const cameraId = formData[`cameraId_${directionIndex}_${order}`] || cameraSetting.cameraId;
        if (!cameraId) {
            setScenarioOptions([]);
            return;
        }
        const camera = props.cameraList.find(cam => cam.value === cameraId);
        if (camera) {
            const scenarios = []
            for (let i = 1; i <= 10; i++) {
                if (camera.data[`trafficVolumeType${i}`] && camera.data[`trafficVolumeType${i}`] == 'VEHICLE') {
                    scenarios.push({ value: i.toString(), label: camera.data[`trafficVolumeName${i}`].toString() });
                }
            }
            setScenarioOptions(scenarios);
        }
    }, [cameraSetting.cameraId, props.cameraList, formData[`cameraId_${directionIndex}_${order}`]]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(cameraDropdownRefs.current).forEach((key) => {
                if (cameraDropdownRefs.current[key] && !cameraDropdownRefs.current[key].contains(event.target)) {
                    setCameraDropdownOpen(prev => ({ ...prev, [key]: false }));
                    setCameraSearchTerm(prev => ({ ...prev, [key]: '' }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    const focusSearchInput = useCallback((fieldKey) => {
        if (cameraDropdownOpen[fieldKey] && cameraSearchInputRefs.current[fieldKey]) {
            setTimeout(() => {
                cameraSearchInputRefs.current[fieldKey]?.focus();
            }, 100);
        }
    }, [cameraDropdownOpen]);

    useEffect(() => {
        Object.keys(cameraDropdownOpen).forEach(key => {
            if (cameraDropdownOpen[key]) {
                focusSearchInput(key);
            }
        });
    }, [cameraDropdownOpen, focusSearchInput]);

    const handleCameraDropdownToggle = useCallback((fieldKey) => {
        setCameraDropdownOpen(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
    }, []);

    const handleCameraSearchTermChange = useCallback((fieldKey, value) => {
        setCameraSearchTerm(prev => ({ ...prev, [fieldKey]: value }));
    }, []);

    const toggleCameraOption = (fieldName, value) => {
        setFormData(prev => ({ ...prev, [fieldName]: value, [`scenarioId_${directionIndex}_${order}`]: '' }));
        // Reset the scenario dropdown value
        const scenarioSelect = document.querySelector(`select[name="scenarioId_${directionIndex}_${order}"]`);
        if (scenarioSelect) {
            scenarioSelect.value = '';
        }
    };

    const onRemoveCameraSetting = () => {
        if (props.onRemoveCameraSetting) {
            props.onRemoveCameraSetting(order);
        }
    };

    const getFilteredCameraOptions = useCallback((fieldKey) => {
        const searchTerm = (cameraSearchTerm[fieldKey] || '').toLowerCase();
        if (!searchTerm) return props.cameraList;
        return props.cameraList.filter(opt => 
            opt.label.toLowerCase().includes(searchTerm)
        );
    }, [cameraSearchTerm, props.cameraList]);

    return (
        <div className='tmd__camera-section'>
            <div className='tmd__direction'>
                <div className="tmd__camera-header">
                    {order === 1 && isEditable &&
                        <button className="tmd__camera-remove-btn" type='button'
                            onClick={onRemoveCameraSetting} title="Remove Camera Setting">
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    }
                </div>
                {/* NO */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.NO}
                    </div>
                    <input type="hidden" name={`no_${directionIndex}_${order}`} value={(order + 1).toString()} />
                    <div className="tmd__label-value">
                        {(order + 1).toString()}
                    </div>
                </section>
                
                {/* Camera Base */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.CAMERA_BASE}
                        <label className="tmd__required">*</label>
                    </div>
                    <div className="full_width" style={{ position: 'relative' }} ref={el => cameraDropdownRefs.current[`cameraId_${directionIndex}_${order}`] = el}>
                        {/* Trigger */}
                        <div
                            onClick={() => handleCameraDropdownToggle(`cameraId_${directionIndex}_${order}`)}
                            className="tmd__select tmd__dropdown-trigger"
                            style={{pointerEvents: !isEditable ? 'none' : 'auto', backgroundColor: !isEditable ? '#f5f5f5' : 'white'}}
                        >
                            <span>{(props.cameraList || []).find(opt => opt.value === (formData[`cameraId_${directionIndex}_${order}`] || cameraSetting.cameraId))?.label || 'カメラを選択してください'}</span>
                            <span className="tmd__dropdown-arrow">
                                <FontAwesomeIcon icon={faChevronDown} />
                            </span>
                        </div>
                        {/* Hidden input to store the selected value */}
                        <input
                            type="hidden"
                            name={`cameraId_${directionIndex}_${order}`}
                            value={formData[`cameraId_${directionIndex}_${order}`] || cameraSetting.cameraId || ''}
                        />
                        {/* Dropdown */}
                        {cameraDropdownOpen[`cameraId_${directionIndex}_${order}`] && (
                            <div className="tmd__dropdown">
                                {/* Search Input */}
                                <div className="tmd__search-container">
                                    <input
                                        ref={el => cameraSearchInputRefs.current[`cameraId_${directionIndex}_${order}`] = el}
                                        type="text"
                                        placeholder="カメラ名で検索..."
                                        value={cameraSearchTerm[`cameraId_${directionIndex}_${order}`] || ''}
                                        onChange={(e) => handleCameraSearchTermChange(`cameraId_${directionIndex}_${order}`, e.target.value)}
                                        className="tmd__search-input"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                <div className="tmd__dropdown-content">
                                    {/* Filtered Options */}
                                    {getFilteredCameraOptions(`cameraId_${directionIndex}_${order}`).length === 0 && cameraSearchTerm[`cameraId_${directionIndex}_${order}`] ? (
                                        <div className="tmd__no-results">検索結果がありません</div>
                                    ) : (
                                        getFilteredCameraOptions(`cameraId_${directionIndex}_${order}`).map((opt) => (
                                            <label key={opt.value} style={{ display: 'block' }}>
                                                <input
                                                    type="radio"
                                                    name={`cameraId_${directionIndex}_${order}_radio`}
                                                    value={opt.value}
                                                    checked={(formData[`cameraId_${directionIndex}_${order}`] || cameraSetting.cameraId || '') === opt.value}
                                                    onChange={() => {
                                                        toggleCameraOption(`cameraId_${directionIndex}_${order}`, opt.value);
                                                        setCameraDropdownOpen(prev => ({ ...prev, [`cameraId_${directionIndex}_${order}`]: false }));
                                                        if (props.setSelectedCamera) {
                                                            props.setSelectedCamera(prev => ({ ...prev, [order]: opt.value }));
                                                        }       
                                                    }}
                                                />
                                                {opt.label}
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Scenario No */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.SCENARIO_NO}
                        <label className="tmd__required">*</label>
                    </div>
                    <select
                        className="tmd__select"
                        name={`scenarioId_${directionIndex}_${order}`}
                        value={formData[`scenarioId_${directionIndex}_${order}`] !== undefined ? formData[`scenarioId_${directionIndex}_${order}`] : (cameraSetting.scenarioId || '')}
                        disabled={!isEditable}
                        style={{backgroundColor: !isEditable ? '#f5f5f5' : 'white'}}
                        onChange={(e) => {
                            const selectedOption = scenarioOptions.find(opt => opt.value === e.target.value);
                            setFormData(prev => ({ 
                                ...prev, 
                                [`scenarioId_${directionIndex}_${order}`]: e.target.value,
                                [`scenarioLabel_${directionIndex}_${order}`]: selectedOption?.label || ''
                            }));
                        }}
                    >
                        <option value="" disabled>選択してください</option>
                        {scenarioOptions && scenarioOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </section>
                <input
                    type="hidden"
                    name={`scenarioLabel_${directionIndex}_${order}`}
                    value={ formData[`scenarioLabel_${directionIndex}_${order}`] || cameraSetting.scenarioLabel || ''}
                />

                {/* Daytime Caution */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.DAYTIME_CAUTION}
                        <label className="tmd__required">*</label>
                    </div>
                    <InputThreshold value={cameraSetting.daytimeCaution || ''}
                        name={`daytimeCaution_${directionIndex}_${order}`}
                        isEditable={isEditable}
                    />
                </section>

                {/* Daytime Warning */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.DAYTIME_WARNING}
                        <label className="tmd__required">*</label>
                    </div>
                    <InputThreshold value={cameraSetting.daytimeWarning || ''}
                        name={`daytimeWarning_${directionIndex}_${order}`}
                        isEditable={isEditable}
                    />
                </section>

                {/* Nighttime Caution */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.NIGHTTIME_CAUTION}
                        <label className="tmd__required">*</label>
                    </div>
                    <InputThreshold value={cameraSetting.nighttimeCaution || ''}
                        name={`nighttimeCaution_${directionIndex}_${order}`}
                        isEditable={isEditable}
                    />
                </section>

                {/* Nighttime Warning */}
                <section className="tmd__form-section">
                    <div className="tmd__label">
                        {TRAFFIC_MONITORING_PROPERTIES.NIGHTTIME_WARNING}
                        <label className="tmd__required">*</label>
                    </div>
                    <InputThreshold value={cameraSetting.nighttimeWarning || ''}
                        name={`nighttimeWarning_${directionIndex}_${order}`}
                        isEditable={isEditable}
                    />
                </section>
            </div>
        </div>
    )
}

export default CameraSetting;